// @flow

// force flow to type check this file - https://github.com/zapier/zapier/pull/31127
import React /* eslint-disable-line no-unused-vars */, {
  type Node,
  useState,
} from 'react';

import { Zindexes } from '@zapier/theme';
import { Colors, Link } from '@zapier/design-system';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

import { type Section, Submenu } from './Submenu';

/** @jsx isolateComponent */

type Props = {|
  /**
   * Content to render within the link.
   */
  children: Node,
  /**
   * The `href` attribute to apply.
   */
  href: string,
  /**
   * Indicates whether the link is selected.
   */
  isSelected: boolean,
  /**
   * Indicates whether submenu for a link is to be rendered
   */
  shouldRenderSubmenu: boolean,
  /**
   * Links to display in the dropdown menu.
   */
  submenu?: Array<Section>,
|};

const Styles = labelStyles(__filename, {
  root: ({ hasDropdown, isSelected }) => [
    {
      color: Colors.grey700,
      fontWeight: 600,
      '&:hover, &:focus': {
        color: Colors.black,
      },
    },
    isSelected &&
      withModifier('selected', {
        color: Colors.black,
        fontWeight: 700,
      }),
    hasDropdown &&
      withModifier('hasDropdown', {
        position: 'relative',
        // Render invisible hover space so that unhovering doesn't
        // occur immediately when mousing off the visible node.
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: -20,
          bottom: 0,
          left: -20,
        },
      }),
  ],
  linkContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
  summary: {
    height: '100%',
    width: '1px',
    opacity: 0,
    position: 'relative',
    // Hides default icon on details element
    '&::-webkit-details-marker': {
      display: 'none',
    },
    // Hide default icon from IE.
    '&::before': {
      display: 'none',
    },
  },
  submenu: {
    background: Colors.white,
    boxShadow: '0 20px 20px 0 rgba(0, 0, 0, 0.2)',
    borderRadius: '0 0 10px 10px',
    left: 0,
    pointerEvents: 'all',
    position: 'absolute',
    top: '60px',
    width: '100%',
    zIndex: Zindexes.universalTopbarMegaDropdown,
    // Covers the gap b/w the link and the dropdown so that
    // the hover state on the element is not lost
    '&::before': {
      content: '""',
      height: '25px',
      position: 'absolute',
      top: '-25px',
      width: 'inherit',
    },
  },
});

/**
 * Wrapper around `Link` specifically for use within `UniversalTopbar`.
 */
export const UniversalTopbarLink = (props: Props) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const closeMenu = () => setIsMenuOpen(false);
  const openMenu = () => setIsMenuOpen(true);

  return props.submenu && props.shouldRenderSubmenu ? (
    <div css={Styles.linkContainer}>
      <Link
        color={null}
        css={Styles.root({ hasDropdown: true, isSelected: props.isSelected })}
        href={props.href}
        onMouseEnter={openMenu}
        onMouseLeave={closeMenu}
      >
        {props.children}
      </Link>
      <details
        onMouseEnter={openMenu}
        onMouseLeave={closeMenu}
        open={isMenuOpen}
      >
        <summary
          aria-haspopup="menu"
          aria-label={`Submenu with additional links`}
          css={Styles.summary}
          role="button"
          tabIndex="0"
        ></summary>
        <div css={Styles.submenu} role="menu">
          <Submenu sections={props.submenu || []} />
        </div>
      </details>
    </div>
  ) : (
    <Link
      color={null}
      css={Styles.root({ hasDropdown: false, isSelected: props.isSelected })}
      href={props.href}
    >
      {props.children}
    </Link>
  );
};

UniversalTopbarLink.defaultProps = {
  shouldRenderSubmenu: false,
};

export default UniversalTopbarLink;
